import { getApolloClient } from '@faustwp/core';
import { SEO } from '../../components';
import { Main } from '../../components';
import {
  Header,
  Footer,
  HeroSection,
  ServicesSection,
  FeaturedWorkSection,
  TechnologySection,
  TeamCultureSection,
  BlogPreviewSection,
  FinalCTASection,
} from '../../components/sections';
import {
  GET_FRONT_PAGE_DATA,
  FRONT_PAGE_VARIABLES,
  GET_FEATURED_CASE_IMAGES_FROM_POSTS,
  getFeaturedCaseImageUrls,
  mapRecentPostsToPreview,
} from '../../lib/queries/frontPage';
import { fetchPageSettings } from '../../lib/queries/pageSettings';
import { fetchLogoPosts } from '../../lib/logoUtils';
import { fetchBlogListData } from '../../lib/blogUtils';
import { getFeaturedCaseImageUrlsFromRest } from '../../lib/wordpressRest';
import { getCanonicalUrl, buildHreflangTags } from '../../lib/siteUrl';

const FALLBACK_EN = {
  metaTitle: 'Home | Identio',
  metaDescription:
    'Your trusted partner in software and digital development. We integrate seamlessly into your development team and bring measurable results.',
  heroTitle: 'Your trusted partner in software and digital development.',
  heroDescription:
    'We integrate seamlessly into your development team. We bring peace of mind and measurable results to your business.',
  heroPrimaryCta: 'Contact',
  heroPrimaryCtaUrl: '/contact',
  heroSecondaryCta: 'Our Work',
  heroSecondaryCtaUrl: '/work',
};

/**
 * English home page at /en. Finnish home is at / (root).
 * Uses getStaticProps + ISR for fast navigation; revalidates every 60s.
 */
export default function EnHomePage({
  homeLogos = [],
  featuredCaseImageUrls = {},
  blogPosts = [],
  homeSettings = null,
}) {
  const s = homeSettings ?? {};
  const title = s.seoTitle ?? s.metaTitle ?? s.meta_title ?? FALLBACK_EN.metaTitle;
  const description = s.seoDescription ?? s.metaDescription ?? s.meta_description ?? FALLBACK_EN.metaDescription;
  const heroTitle = s.heroTitle ?? s.hero_title ?? FALLBACK_EN.heroTitle;
  const heroDescription = s.heroDescription ?? s.hero_description ?? FALLBACK_EN.heroDescription;
  const primaryCta = s.heroPrimaryCta ?? s.hero_primary_cta ?? FALLBACK_EN.heroPrimaryCta;
  const primaryHref = s.heroPrimaryCtaUrl ?? s.hero_primary_cta_url ?? FALLBACK_EN.heroPrimaryCtaUrl;
  const secondaryCta = s.heroSecondaryCta ?? s.hero_secondary_cta ?? FALLBACK_EN.heroSecondaryCta;
  const secondaryHref = s.heroSecondaryCtaUrl ?? s.hero_secondary_cta_url ?? FALLBACK_EN.heroSecondaryCtaUrl;

  return (
    <>
      <SEO title={title} description={description} url={getCanonicalUrl('/en')} alternates={buildHreflangTags('/', '/en')} />
      <Header />
      <Main>
        <HeroSection
          variant="home"
          logos={homeLogos}
          title={heroTitle}
          description={heroDescription}
          primaryCta={primaryCta}
          primaryHref={primaryHref}
          secondaryCta={secondaryCta}
          secondaryHref={secondaryHref}
        />
        <ServicesSection variant="home" />
        <FeaturedWorkSection
          featuredCaseImageUrls={featuredCaseImageUrls}
          caseStudyLinkBase="work"
        />
        <TechnologySection variant="home" />
        <TeamCultureSection />
        <BlogPreviewSection posts={blogPosts} />
        <FinalCTASection variant="simple" />
      </Main>
      <Footer />
    </>
  );
}

export async function getStaticProps() {
  const client = getApolloClient();
  let homeLogos = [];
  let featuredCaseImageUrls = {};
  let blogPosts = [];

  try {
    await client.query({
      query: GET_FRONT_PAGE_DATA,
      variables: FRONT_PAGE_VARIABLES,
    });
  } catch (err) {
    console.error('[en/index] getStaticProps menu/settings query failed', err);
  }

  let homeSettings = null;
  try {
    homeSettings = await fetchPageSettings(client, 'en') ?? await fetchPageSettings(client, 'home');
  } catch (e) {
    // ACF optional
  }

  try {
    const [{ logos }, featuredData, blogData] = await Promise.all([
      fetchLogoPosts(client),
      client.query({ query: GET_FEATURED_CASE_IMAGES_FROM_POSTS }).then((r) => r.data).catch(() => ({})),
      fetchBlogListData(client, { first: 3 }, { lang: 'en' }),
    ]);
    if (logos?.length) homeLogos = [...logos].reverse().slice(0, 8);
    featuredCaseImageUrls = getFeaturedCaseImageUrls(featuredData) ?? {};
    if (!featuredCaseImageUrls?.mehilainen && !featuredCaseImageUrls?.visma && !featuredCaseImageUrls?.smartum) {
      try {
        featuredCaseImageUrls = (await getFeaturedCaseImageUrlsFromRest()) ?? {};
      } catch {
        // REST fallback failed, keep empty
      }
    }
    const posts = blogData?.posts ?? [];
    blogPosts = mapRecentPostsToPreview(posts.slice(0, 3), '/blog');
  } catch (err) {
    console.error('[en/index] getStaticProps data failed', err);
  }

  return {
    props: {
      homeLogos,
      featuredCaseImageUrls,
      blogPosts,
      homeSettings,
    },
    revalidate: 60,
  };
}
